import React, { useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useChangePasswordMutation } from '@@/services/auth';
import StandardInput from '@@/components/Inputs/StandardInput';
import { Key, RefreshCw, ArrowLeft, X, Lock } from 'lucide-react';

const schema = yup
  .object({
    password: yup.string().required('Veuillez entrer votre mot de passe'),
  })
  .required();

const ChangePasswordPage: React.FC = () => {
  const navigation = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('k') as string;

  const {
    register,
    reset,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm<{ password: string }>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const [changePassword, { isSuccess, isError }] = useChangePasswordMutation();

  const onSubmit = useCallback(
    (data: { password: string }) => {
      changePassword({ ...data, token });
    },
    [changePassword, token],
  );

  useEffect(() => {
    if (isSuccess) {
      reset();
      toast.success('Votre mot de passe a bien été modifié');
      navigation('/signin');
    }
  }, [isSuccess, reset, navigation]);

  useEffect(() => {
    if (!token) {
      navigation('/reset-password');
    }
  }, [token, navigation]);

  if (isError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-blue-50 to-primary-100">
        <div className="py-8 flex items-center justify-center">
          <div className="w-full max-w-md px-4">
            {/* Header d'erreur */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-danger-500 rounded-lg mb-3">
                <X className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-neutral-900 mb-1">
                Lien expiré
              </h1>
              <p className="text-neutral-600 text-sm">
                Ce lien de réinitialisation n'est plus valide
              </p>
            </div>

            {/* Card d'erreur */}
            <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-6">
              <p className="text-neutral-600 text-sm text-center mb-6 leading-relaxed">
                La requête a expiré. Veuillez refaire une demande de
                réinitialisation de mot de passe.
              </p>

              <Link
                to="/reset-password"
                className="w-full inline-flex items-center justify-center px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-button transition-all duration-200 text-sm"
              >
                <ArrowLeft size={16} className="mr-2" />
                Nouvelle demande
              </Link>

              {/* Footer */}
              <div className="mt-4 pt-4 border-t border-neutral-200 text-center">
                <Link
                  to="/signin"
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
                >
                  Retour à la connexion
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="py-8 flex items-center justify-center">
        <div className="w-full max-w-md px-4">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-600 rounded-lg mb-3">
              <Key className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-1">
              Nouveau mot de passe
            </h1>
            <p className="text-neutral-600 text-sm">
              Sécurisez votre compte avec un nouveau mot de passe
            </p>
          </div>

          {/* Formulaire */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <p className="text-center text-gray-600 text-sm mb-6 leading-relaxed">
              Veuillez entrer un nouveau mot de passe sécurisé
            </p>

            <div className="space-y-4">
              {/* Nouveau mot de passe */}
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <Lock size={16} className="text-primary-600" />
                  <label className="text-sm font-medium text-neutral-700">
                    Nouveau mot de passe *
                  </label>
                </div>
                <StandardInput
                  register={register}
                  id="password"
                  label=""
                  placeholder="Votre nouveau mot de passe"
                  error={errors?.password}
                  type="password"
                />
              </div>

              {/* Conseils de sécurité */}
              <div className="bg-primary-50 rounded-lg p-3">
                <p className="text-xs text-neutral-600">
                  <strong>Conseils :</strong> Utilisez au moins 8 caractères
                  avec des lettres, chiffres et symboles
                </p>
              </div>

              {/* Bouton de validation */}
              <button
                type="button"
                className={`
                  w-full py-2.5 px-4 rounded-lg font-semibold text-white text-sm
                  transition-all duration-200 
                  ${
                    isSubmitting
                      ? 'bg-neutral-400 cursor-not-allowed'
                      : 'bg-primary-600 hover:bg-primary-700 shadow-md hover:shadow-lg'
                  }
                `}
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <RefreshCw size={16} className="animate-spin" />
                    <span>Modification...</span>
                  </div>
                ) : (
                  <span>Valider le nouveau mot de passe</span>
                )}
              </button>
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-neutral-200 text-center">
              <Link
                to="/signin"
                className="inline-flex items-center gap-2 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <ArrowLeft size={14} />
                Retour à la connexion
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
